/**
 * Wishlist toggling.
 *
 * Updates optimistically so the heart fills the instant it is clicked, then
 * reconciles with the server response — and rolls back if the request fails.
 */

import { $$, delegate } from '@/lib/dom'
import { api } from '@/lib/api'
import { app } from '@/store/app'
import { toast } from './toast'

function paint(ids: number[]): void {
  $$('[data-wishlist-toggle]').forEach((button) => {
    const productId = Number(button.dataset.wishlistToggle)
    const active = ids.includes(productId)

    button.classList.toggle('is-active', active)
    button.setAttribute('aria-pressed', String(active))
    button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist')
  })

  $$('[data-wishlist-count]').forEach((badge) => {
    badge.textContent = String(ids.length)
    badge.hidden = ids.length === 0
  })
}

export function initWishlist(): void {
  app.subscribe((state) => paint(state.wishlistIds))

  delegate(document, 'click', '[data-wishlist-toggle]', (event, button) => {
    event.preventDefault()
    event.stopPropagation() // the whole product card is a link — do not follow it

    const productId = Number(button.dataset.wishlistToggle)
    if (!Number.isFinite(productId)) return

    const before = app.get().wishlistIds
    const optimistic = before.includes(productId)
      ? before.filter((id) => id !== productId)
      : [...before, productId]

    app.set({ wishlistIds: optimistic })

    void api
      .toggleWishlist(productId)
      .then((state) => {
        app.set({ wishlistIds: state.ids })
        toast(state.message, state.added ? 'success' : 'info')

        // The wishlist page lists only saved items, so a removal must drop the
        // card rather than leave an empty outline behind.
        if (document.body.dataset.page === 'wishlist' && !state.added) {
          document.querySelector(`[data-product-card="${productId}"]`)?.remove()
          if (state.ids.length === 0) window.location.reload()
        }
      })
      .catch(() => {
        app.set({ wishlistIds: before }) // roll back
        toast('Could not update your wishlist.', 'error')
      })
  })
}
